import { getAdobeAccessToken } from '../auth/adobeIms';
import { resolveRegion } from './regionConfig';
import { daysBetween, parseWfDate } from './date';
import type { Deal, Touchpoint, AccessRequest } from '../types';


export const DB_ACTION_BASE_URL = 'https://55338-809mauveporcupine.adobeioruntime.net/api/v1/web/ondemand-dashboard';

const REPORT_ID = 'all-ondemand-global-requests';
const TOUCHPOINT_REPORT_ID = 'all-ondemand-global-touchpoints';

// Pagination constants — sized so each page stays well under Adobe IO Runtime's 1 MB result limit.
const PAGE_SIZE = 1000;


function quarterFromDate(dateStr: string): { fy: string; q: string } {
  if (!dateStr) return { fy: '', q: '' };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { fy: '', q: '' };
  const month = d.getMonth() + 1;
  const q = month <= 3 ? 'Q1' : month <= 6 ? 'Q2' : month <= 9 ? 'Q3' : 'Q4';
  return { fy: String(d.getFullYear()), q };
}

const STATUS_MAP: Record<string, string> = { CNL: 'Cancelled' };

export function normalizeRawRecord(raw: Record<string, unknown>): Deal {
  const entry = String(raw.request_time ?? ''); // Instead of entry, use request_time
  const completion = String(raw.completion ?? '');
  const { fy, q } = quarterFromDate(entry);
  const market_area = raw.market_area !== undefined ? String(raw.market_area) : undefined;
  // const segmentForSubRegion = raw.segment_sales ?? raw.segment;
  const segment_sales = raw.segment_sales !== undefined ? String(raw.segment_sales) : undefined;
  const resolved = resolveRegion(market_area, segment_sales, String(raw.segment ?? ''));
  const status = STATUS_MAP[String(raw.status ?? '')] ?? String(raw.status ?? '');

  // Age: In Progress → days from entry to today; others → days from entry to completion
  let age: number | null;
  if (status === 'In Progress' && entry) {
    const entryDate = parseWfDate(entry);
    if (entryDate) {
      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      age = Math.round((Date.now() - entryDate.getTime()) / MS_PER_DAY);
    } else {
      age = null;
    }
  } else {
    age = daysBetween(entry, completion);
  }

  return {
    datasetType: 'request',
    id: String(raw.id ?? ''),
    account: String(raw.account ?? ''),
    sc: String(raw.sc ?? ''),
    status,
    status_eq: String(raw.status_eq ?? raw.status ?? ''),
    age,
    gnarr: parseFloat(String(raw.gnarr ?? '0')) || 0,
    region: resolved?.region ?? String(raw.region ?? ''),
    sub_region: resolved?.sub_region ?? String(raw.sub_region ?? raw.subregion ?? ''),
    close_q: String(raw.close_q ?? ''),
    offering: String(raw.offering ?? ''),
    requestName: String(raw.requestName ?? ''),
    requestID: String(raw.requestID ?? ''),
    products: String(raw.products ?? ''),
    entry,
    completion,
    sales_region: String(raw.sales_region ?? ''),
    adjusted_commit: String(raw.adjusted_commit ?? ''),
    opp_status: String(raw.opp_status ?? ''),
    opp_gnarr: parseFloat(String(raw.opp_gnarr ?? '0')) || 0,
    created_q: String(raw.created_q ?? q),
    created_fy: String(raw.created_fy ?? fy),
    raw_sub: raw.raw_sub !== undefined ? String(raw.raw_sub) : undefined,
    segment: raw.segment !== undefined ? String(raw.segment) : undefined,
    segment_sales,
    market_area,
  };
}

function extractPlainText(draftJsJson: string): string {
  try {
    const parsed = JSON.parse(draftJsJson);
    if (parsed?.blocks && Array.isArray(parsed.blocks)) {
      return parsed.blocks.map((b: { text?: string }) => b.text || '').join(' ').trim();
    }
  } catch {
    // Not valid JSON — return as-is
  }
  return draftJsJson;
}

export function normalizeTouchpoint(raw: Record<string, unknown>): Touchpoint {
  const entry = String(raw.entry ?? '');
  const completion = String(raw.completion ?? '');
  const solutionRaw = String(raw.solutionComments ?? '');
  const commentText = solutionRaw && solutionRaw.startsWith('{') ? extractPlainText(solutionRaw) : solutionRaw;
  return {
    datasetType: 'touchpoint',
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    account: String(raw.account ?? ''),
    sc: String(raw.sc ?? ''),
    status: String(raw.status ?? ''),
    age: raw.age !== null && raw.age !== undefined && raw.age !== '' ? Number(raw.age) : null,
    gnarr: parseFloat(String(raw.gnarr ?? '0')) || 0,
    region: String(raw.region ?? ''),
    sub_region: String(raw.raw_sub ?? ''),
    close_q: String(raw.close_q ?? ''),
    offering: String(raw.offering ?? ''),
    entry,
    completion,
    actualStartDate: String(raw.actualStartDate ?? ''),
    task_id: String(raw.task_id ?? ''),
    touchpointType: String(raw.touchpointType ?? ''),
    solutionComments: solutionRaw,
    commentText,
    market_area: raw.market_area !== undefined ? String(raw.market_area) : undefined,
    segment: raw.segment !== undefined ? String(raw.segment) : undefined,
    segment_sales: raw.segment_sales !== undefined ? String(raw.segment_sales) : undefined,
    raw_sub: raw.raw_sub !== undefined ? String(raw.raw_sub) : undefined,
    created_fy: String(raw.created_fy ?? ''),
    created_q: String(raw.created_q ?? ''),
    opp_gnarr: parseFloat(String(raw.opp_gnarr ?? '0')) || 0,
  };
}

export interface UserRecord {
  email: string;
  name: string | null;
  accessLevel: AdobeImsAccessLevel;
  enabled: boolean;
  createdAt?: string;
  lastUpdated?: string;
  lastLoginAt?: string;
  updatedBy?: string;
}

export interface AdminUserRecord extends UserRecord {
  _id: string;
}

export interface AuditLogEntry {
  _id: string;
  timestamp: string;
  actionType: string;
  email: string;
  accessLevel?: string;
  [key: string]: unknown;
}

export interface AuditLogResponse {
  logs: AuditLogEntry[];
  totalCount: number;
  offset: number;
  limit: number;
}

export interface ReportData {
  records: Deal[];
  count: number;
  totalCount: number;
  lastSyncedAt: string | null;
  fromCache?: boolean;
  stale?: boolean;
  syncError?: string;
}

export interface TouchpointReportData {
  records: Touchpoint[];
  count: number;
  totalCount: number;
  lastSyncedAt: string | null;
  fromCache?: boolean;
  stale?: boolean;
  syncError?: string;
}

export interface SyncResult {
  synced: boolean;
  count: number;
  lastSyncedAt: string;
  syncCounts?: {
    teams: number;
    users: number;
    assignments: number;
    tasks: number;
    issues: number;
    projects: number;
  };
  stale?: boolean;
  syncError?: string;
}

export function actionUrl(action: string): string {
  return `${DB_ACTION_BASE_URL.replace(/\/$/, '')}/${action}`;
}

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchUserRecord(userEmail: string): Promise<UserRecord | null> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('db-get-user'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ email: userEmail }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data?.found) return null;
    return data.user as UserRecord;
  } catch (error) {
    console.error('fetchUserRecord error:', error);
    return null;
  }
}



export async function fetchDeals(reportId = REPORT_ID): Promise<ReportData | null> {
  try {
    const token = getAdobeAccessToken();
    const headers = authHeaders(token || '');

    // Phase 1: fetch first page to discover totalCount
    const firstPageResp = await fetch(actionUrl('get-report-data'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ reportId, offset: 0, limit: PAGE_SIZE }),
    });
    if (!firstPageResp.ok) {
      console.error(`fetchDeals HTTP ${firstPageResp.status}`);
      return null;
    }
    const firstPage = await firstPageResp.json();
    const totalCount: number = firstPage.totalCount ?? firstPage.data?.length ?? 0;
    let allRecords: Record<string, unknown>[] = Array.isArray(firstPage.data) ? firstPage.data : [];

    // Phase 2: fetch all remaining pages concurrently
    if (totalCount > PAGE_SIZE) {
      const totalPages = Math.ceil(totalCount / PAGE_SIZE);
      const remainingOffsets: number[] = [];
      for (let page = 1; page < totalPages; page++) {
        remainingOffsets.push(page * PAGE_SIZE);
      }

      const pageResponses = await Promise.all(
        remainingOffsets.map(offset =>
          fetch(actionUrl('get-report-data'), {
            method: 'POST',
            headers,
            body: JSON.stringify({ reportId, offset, limit: PAGE_SIZE }),
          }).then(async (resp) => {
            if (!resp.ok) throw new Error(`fetchDeals page offset=${offset} failed: HTTP ${resp.status}`);
            return resp.json();
          })
        )
      );

      for (const page of pageResponses) {
        if (Array.isArray(page.data)) {
          allRecords = allRecords.concat(page.data);
        }
      }
    }

    return {
      records: allRecords.map((r: Record<string, unknown>) => normalizeRawRecord(r)),
      count: allRecords.length,
      totalCount,
      lastSyncedAt: firstPage.lastSyncedAt || null,
      fromCache: firstPage.fromCache,
      stale: firstPage.stale,
      syncError: firstPage.syncError,
    };
  } catch (error) {
    console.error('fetchDeals error:', error);
    return null;
  }
}

export async function fetchTouchpoints(reportId = TOUCHPOINT_REPORT_ID): Promise<TouchpointReportData | null> {
  try {
    const token = getAdobeAccessToken();
    const headers = authHeaders(token || '');

    const firstPageResp = await fetch(actionUrl('get-report-data'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ reportId, offset: 0, limit: PAGE_SIZE }),
    });
    if (!firstPageResp.ok) {
      console.error(`fetchTouchpoints HTTP ${firstPageResp.status}`);
      return null;
    }
    const firstPage = await firstPageResp.json();
    const totalCount: number = firstPage.totalCount ?? firstPage.data?.length ?? 0;
    let allRecords: Record<string, unknown>[] = Array.isArray(firstPage.data) ? firstPage.data : [];

    // Fetch all remaining pages concurrently
    if (totalCount > PAGE_SIZE) {
      const totalPages = Math.ceil(totalCount / PAGE_SIZE);
      const remainingOffsets: number[] = [];
      for (let page = 1; page < totalPages; page++) {
        remainingOffsets.push(page * PAGE_SIZE);
      }

      const pageResponses = await Promise.all(
        remainingOffsets.map(offset =>
          fetch(actionUrl('get-report-data'), {
            method: 'POST',
            headers,
            body: JSON.stringify({ reportId, offset, limit: PAGE_SIZE }),
          }).then(async (resp) => {
            if (!resp.ok) throw new Error(`fetchTouchpoints page offset=${offset} failed: HTTP ${resp.status}`);
            return resp.json();
          })
        )
      );

      for (const page of pageResponses) {
        if (Array.isArray(page.data)) {
          allRecords = allRecords.concat(page.data);
        }
      }
    }

    return {
      records: allRecords.map((r: Record<string, unknown>) => normalizeTouchpoint(r)),
      count: allRecords.length,
      totalCount,
      lastSyncedAt: firstPage.lastSyncedAt || null,
      fromCache: firstPage.fromCache,
      stale: firstPage.stale,
      syncError: firstPage.syncError,
    };
  } catch (error) {
    console.error('fetchTouchpoints error:', error);
    return null;
  }
}

const SYNC_POLL_INTERVAL_MS = 3000;
const SYNC_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export async function triggerSync(reportId = REPORT_ID): Promise<SyncResult | null> {
  try {
    const token = getAdobeAccessToken();

    // 1. Fire async worker — returns activationId immediately
    const startResp = await fetch(actionUrl('sync-report-trigger'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ reportId }),
    });
    if (!startResp.ok) {
      const errBody = await startResp.json().catch(() => ({ error: 'unknown' }));
      throw new Error(errBody.error || `HTTP ${startResp.status}`);
    }
    const startData = await startResp.json();
    const activationId: string = startData.activationId;
    if (!activationId) {
      throw new Error('sync-report-trigger did not return an activationId');
    }

    // 2. Poll until complete, error, or timeout
    const deadline = Date.now() + SYNC_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise<void>(r => setTimeout(r, SYNC_POLL_INTERVAL_MS));

      const pollResp = await fetch(actionUrl('sync-status-check'), {
        method: 'POST',
        headers: authHeaders(token || ''),
        body: JSON.stringify({ activationId }),
      });
      if (!pollResp.ok) {
        console.warn(`triggerSync: poll returned ${pollResp.status}, retrying`);
        continue;
      }

      const poll = await pollResp.json();
      if (poll.status === 'complete') {
        // Do NOT read poll.data here — the full dataset can exceed 1 MB.
        // The caller should trigger a separate paginated fetch (refreshDeals)
        // after receiving this signal.
        return {
          synced: true,
          count: poll.count || 0,
          lastSyncedAt: poll.lastSyncedAt || new Date().toISOString(),
          syncCounts: poll.syncCounts,
          stale: false,
        };
      }
      if (poll.status === 'error') {
        throw new Error(poll.error || 'Sync failed');
      }
      // status === 'running' — keep polling
    }

    throw new Error('Sync timed out — the job is still running in the background');
  } catch (error) {
    console.error('triggerSync error:', error);
    throw error;
  }
}

export interface EligibleCountsResult {
  counts: Record<string, unknown>;
  heatmapDenominator: Record<string, number>;
  reportId: string;
  errorCount: number;
  lastSyncedAt?: string;
}

export interface CachedDenominatorsResult {
  heatmapDenominator: Record<string, number>;
  lastSyncedAt: string | null;
}

export async function fetchCachedDenominators(reportId: string): Promise<CachedDenominatorsResult | null> {
  try {
    const token = getAdobeAccessToken();
    const resp = await fetch(actionUrl('get-report-data'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ reportId }),
    });
    if (!resp.ok) return null;
    const body = await resp.json();
    if (!body?.heatmapDenominator) return null;
    return {
      heatmapDenominator: body.heatmapDenominator as Record<string, number>,
      lastSyncedAt: body.lastSyncedAt ?? null,
    };
  } catch (error) {
    console.error('fetchCachedDenominators error:', error);
    return null;
  }
}

export async function fetchEligibleCounts(reportId: string): Promise<EligibleCountsResult | null> {
  try {
    const token = getAdobeAccessToken();
    const resp = await fetch(actionUrl('get-heatmap-eligible-counts'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ reportId }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: 'unknown' }));
      throw new Error((err as { error?: string }).error || `HTTP ${resp.status}`);
    }
    return await resp.json();
  } catch (error) {
    console.error('fetchEligibleCounts error:', error);
    return null;
  }
}

export async function manageUsers(
  action: 'list' | 'upsert' | 'remove',
  payload?: { email?: string; name?: string; accessLevel?: string }
): Promise<unknown> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('db-manage-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ action, ...payload }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('manageUsers error:', error);
    throw error;
  }
}

export type UploadUntrackedMode = 'append' | 'replace';

export interface UploadUntrackedResult {
  mode: UploadUntrackedMode;
  inserted: number;
  skippedCount: number;
  skipped: string[];
}

export async function uploadUntrackedCsv(
  rows: Record<string, string>[],
  mode: UploadUntrackedMode = 'append'
): Promise<UploadUntrackedResult> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('admin-upload-untracked-data'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ rows, mode }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('uploadUntrackedCsv error:', error);
    throw error;
  }
}

export async function fetchUntrackedData(): Promise<Record<string, string>[]> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('admin-export-untracked-data'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }
    const body = await response.json();
    return Array.isArray(body.rows) ? body.rows : [];
  } catch (error) {
    console.error('fetchUntrackedData error:', error);
    throw error;
  }
}

export interface ImsGroup {
  id: string | null;
  name: string | null;
  rawGroupName: string | null;
  role: string | null;
  groupType: string | null;
}

export interface ImsOrg {
  imsOrgId: string;
  orgName: string | null;
  orgType: string | null;
  countryCode: string | null;
  groups: ImsGroup[];
  sourceUserIds?: string[];
}

export interface ImsProfile {
  userId: string;
  userType: string;
  description: string;
  owningOrgId: string | null;
  visible: boolean;
  default: boolean;
  status: string;
  hasLowPriority: boolean;
  isSuppressed: boolean;
}

export interface ImsCatalog {
  profiles: ImsProfile[];
  organizations: ImsOrg[];
  profileOrgMap: Record<string, string[]>;
  count: number;
}

export async function fetchImsOrgs(): Promise<ImsCatalog> {
  const empty: ImsCatalog = { profiles: [], organizations: [], profileOrgMap: {}, count: 0 };
  try {
    const token = getAdobeAccessToken();
    const resp = await fetch(actionUrl('get-ims-orgs'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      // body: JSON.stringify({ client_id: '' }),
    });
    if (!resp.ok) return empty;
    const body = await resp.json() as ImsCatalog;
    return {
      profiles:      body.profiles      ?? [],
      organizations: body.organizations ?? [],
      profileOrgMap: body.profileOrgMap ?? {},
      count:         body.count         ?? 0,
    };
  } catch (error) {
    console.error('fetchImsOrgs error:', error);
    return empty;
  }
}

// ─── Admin User Management ─────────────────────────────────────────────────

export async function adminListUsers(): Promise<AdminUserRecord[]> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('admin-manage-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ operation: 'list' }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    const data = await response.json() as { users: AdminUserRecord[] };
    return data.users ?? [];
  } catch (error) {
    console.error('adminListUsers error:', error);
    throw error;
  }
}

export async function adminUpsertUser(user: {
  email: string;
  name?: string;
  accessLevel: AdobeImsAccessLevel;
}): Promise<AdminUserRecord> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('admin-manage-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ operation: 'upsert', user }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    const data = await response.json() as { user: AdminUserRecord };
    return data.user;
  } catch (error) {
    console.error('adminUpsertUser error:', error);
    throw error;
  }
}

export async function adminDeleteUser(targetEmail: string): Promise<string> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('admin-manage-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ operation: 'delete', targetEmail }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    const data = await response.json() as { deleted: string };
    return data.deleted;
  } catch (error) {
    console.error('adminDeleteUser error:', error);
    throw error;
  }
}

export async function adminToggleUser(
  targetEmail: string,
  enabled: boolean
): Promise<AdminUserRecord> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('admin-manage-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({
        operation: 'upsert',
        user: { email: targetEmail, enabled },
      }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    const data = await response.json() as { user: AdminUserRecord };
    return data.user;
  } catch (error) {
    console.error('adminToggleUser error:', error);
    throw error;
  }
}

// ─── Audit Log ─────────────────────────────────────────────────────────────

export async function adminListAuditLogs(params: {
  offset?: number;
  limit?: number;
  actionType?: string;
  actorEmail?: string;
  startDate?: string;
  endDate?: string;
} = {}): Promise<AuditLogResponse> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('admin-list-audit-logs'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    return await response.json() as AuditLogResponse;
  } catch (error) {
    console.error('adminListAuditLogs error:', error);
    throw error;
  }
}

// ─── Access Requests & Events ──────────────────────────────────────────────

export async function emitEvent(
  eventType: string,
  payload: Record<string, unknown>
): Promise<void> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('event-emit'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ eventType, payload }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
  } catch (error) {
    console.error('emitEvent error:', error);
    throw error;
  }
}

export async function listAccessRequests(
  filters?: { status?: string }
): Promise<AccessRequest[]> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('db-list-access-requests'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify(filters ?? {}),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
    const data = await response.json() as { requests: AccessRequest[] };
    return data.requests ?? [];
  } catch (error) {
    console.error('listAccessRequests error:', error);
    throw error;
  }
}

export async function updateAccessRequest(
  requestId: string,
  status: 'approved' | 'rejected',
  reviewedBy: string
): Promise<void> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('db-update-access-request'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ requestId, status, reviewedBy }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error((errBody as { error?: string }).error || `HTTP ${response.status}`);
    }
  } catch (error) {
    console.error('updateAccessRequest error:', error);
    throw error;
  }
}
